export interface EssDiagnosticEvent {
    schemaVersion: number;
    eventId: string;
    sessionId: string;
    attemptId: string;
    source: 'browser' | 'server' | 'collector';
    sequence: number;
    timestamp: string;
    elapsedMs: number;
    sessionElapsedMs?: number;
    attemptNumber?: number;
    collectorEpoch?: string;
    eventType: string;
    serverConnectionId?: string;
    details: { [key: string]: any };
}

export function diagnosticId(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => ('0' + b.toString(16)).slice(-2)).join('');
}

/** One recorder per worker login; monotonic time orders events despite clock adjustments. */
export class EssDiagnosticRecorder {
    private active: { attemptId: string; sequence: number; started: number; lastFrame: number; attemptNumber: number };
    private firstAttempt = true;
    private attemptNumber = 0;
    private sessionStarted: number;

    constructor(private sessionId: string, private emit: (event: EssDiagnosticEvent) => void,
                private now: () => number = () => performance.now(),
                private utc: () => string = () => new Date().toISOString(),
                private id: () => string = diagnosticId) { this.sessionStarted = this.now(); }

    begin(details: { [key: string]: any }, identity: { [key: string]: any }): string {
        this.active = { attemptId: this.id(), sequence: 0, started: this.now(), lastFrame: null,
            attemptNumber: ++this.attemptNumber };
        if (this.firstAttempt) {
            this.record('session-start', identity);
            this.firstAttempt = false;
        }
        this.record('attempt-started', details);
        return this.active.attemptId;
    }

    frame(): void { this.active.lastFrame = this.now(); }

    capture(): { attemptId: string; record: (type: string, details?: object) => void; frame: () => void } {
        const attempt = this.active;
        return { attemptId: attempt.attemptId,
            record: (type, details = {}) => this.write(attempt, type, details),
            frame: () => { attempt.lastFrame = this.now(); } };
    }

    record(eventType: string, details: { [key: string]: any } = {}): void {
        if (this.active) { this.write(this.active, eventType, details); }
    }

    private write(attempt: { attemptId: string; sequence: number; started: number; lastFrame: number; attemptNumber: number },
                  eventType: string, details: object): void {
        this.emit({ schemaVersion: 1, eventId: this.id(), sessionId: this.sessionId,
            attemptId: attempt.attemptId, source: 'browser', sequence: ++attempt.sequence,
            timestamp: this.utc(), elapsedMs: this.now() - attempt.started,
            sessionElapsedMs: this.now() - this.sessionStarted, attemptNumber: attempt.attemptNumber, eventType,
            details: Object.assign({ lastFrameAgeMs: attempt.lastFrame === null ? null : this.now() - attempt.lastFrame }, details) });
    }
}
